import { useState, useEffect } from 'react'
import styles from './MultipleWorklogEntriesModal.module.css'
import useEscapeKey from '../../hooks/useEscapeKey'
import { moment } from '../../utils/dateUtils'

export default function MultipleWorklogEntriesModal({
    isOpen,
    onClose,
    worklogs = [],
    onSave,
    onDelete,
    projects = [],
    teammates = [],
    currentUser = null
}) {
    const [entries, setEntries] = useState([])
    const [editingEntryId, setEditingEntryId] = useState(null)

    // Handle escape key to close modal
    useEscapeKey(isOpen, onClose)

    // Initialize entries from worklogs
    useEffect(() => {
        if (isOpen && worklogs.length > 0) {
            const formattedEntries = worklogs.map(worklog => ({
                id: worklog.id,
                description: worklog.description || '',
                startTime: formatTimeForInput(worklog.start_time),
                endTime: formatTimeForInput(worklog.end_time),
                isEditing: false,
                originalData: worklog
            }))
            setEntries(formattedEntries)
            setEditingEntryId(null)
        }
    }, [isOpen, worklogs])

    const formatTimeForInput = (date) => {
        if (!date) return ''
        const momentDate = moment(date)
        if (!momentDate.isValid()) return ''
        return momentDate.format('HH:mm')
    }

    const getCommonData = () => {
        if (worklogs.length === 0) return null

        const firstWorklog = worklogs[0]
        const user = firstWorklog.user_id
            ? (teammates.find(t => t.id === firstWorklog.user_id) || currentUser)
            : currentUser

        const project = projects.find(p => p.id === firstWorklog.project?.id)

        return {
            user: user?.display_name || user?.first_name || user?.email || 'Current User',
            project: project ? `${project.name}${project.client_name ? ` - ${project.client_name}` : ''}` : (firstWorklog.project?.name || 'Unknown Project'),
            projectColor: project?.color || '#6B7280',
            task: firstWorklog.task?.name || '',
            date: firstWorklog.date || firstWorklog.start_time,
            billable: firstWorklog.billable
        }
    }

    const handleEditClick = (entryId) => {
        setEntries(entries.map(entry =>
            entry.id === entryId ? { ...entry, isEditing: true } : entry
        ))
        setEditingEntryId(entryId)
    }

    const handleCancelEdit = (entryId) => {
        const entry = entries.find(e => e.id === entryId)

        // If it's a new entry that hasn't been saved, remove it
        if (entry && entry.isNew) {
            setEntries(entries.filter(e => e.id !== entryId))
            setEditingEntryId(null)
            return
        }

        // Otherwise, revert to original data
        setEntries(entries.map(e => {
            if (e.id === entryId) {
                return {
                    ...e,
                    description: e.originalData.description || '',
                    startTime: formatTimeForInput(e.originalData.start_time),
                    endTime: formatTimeForInput(e.originalData.end_time),
                    isEditing: false
                }
            }
            return e
        }))
        setEditingEntryId(null)
    }

    const handleSaveEntry = async (entryId) => {
        const entry = entries.find(e => e.id === entryId)
        if (!entry) return

        // Validation
        if (!entry.description.trim()) {
            alert('Please enter a description')
            return
        }
        if (!entry.startTime || !entry.endTime) {
            alert('Please fill in start and end times')
            return
        }

        const startTimeParts = entry.startTime.split(':').map(Number)
        const [startHour, startMinute] = startTimeParts
        const endTimeParts = entry.endTime.split(':').map(Number)
        const [endHour, endMinute] = endTimeParts

        const originalWorklog = entry.originalData
        const date = originalWorklog.date || originalWorklog.start_time

        const startTime = moment(date)
            .hour(startHour)
            .minute(startMinute)
            .second(0)
            .millisecond(0)
            .toDate()

        let endTime = moment(date)
            .hour(endHour)
            .minute(endMinute)
            .second(0)
            .millisecond(0)
            .toDate()

        // Handle overnight shifts
        if (moment(endTime).isSameOrBefore(moment(startTime))) {
            endTime = moment(endTime).add(1, 'day').toDate()
        }

        const worklogDate = moment(date).startOf('day').toDate()

        // For new entries, use currentUser.id, for existing entries use the worklog's user_id
        const userId = entry.isNew ? (currentUser?.id || null) : originalWorklog.user_id

        const worklogData = {
            id: entry.isNew ? null : entry.id, // Null ID for new entries
            title: entry.description,
            startTime,
            endTime,
            date: worklogDate,
            project: originalWorklog.project?.id || originalWorklog.project,
            task: originalWorklog.task?.id || originalWorklog.task,
            userId: userId,
            billable: originalWorklog.billable,
            type: 'work'
        }

        await onSave(worklogData)

        // Update entry to non-editing state and refresh original data
        setEntries(entries.map(e =>
            e.id === entryId ? {
                ...e,
                isEditing: false,
                isNew: false, // Mark as no longer new after save
                originalData: {
                    ...e.originalData,
                    description: entry.description,
                    start_time: startTime,
                    end_time: endTime
                }
            } : e
        ))
        setEditingEntryId(null)
    }

    const handleDeleteEntry = async (entryId) => {
        const entry = entries.find(e => e.id === entryId)

        // If it's a new unsaved entry, just remove it without confirmation
        if (entry && entry.isNew) {
            const updatedEntries = entries.filter(e => e.id !== entryId)
            setEntries(updatedEntries)
            setEditingEntryId(null)

            // If no more entries, close the modal
            if (updatedEntries.length === 0) {
                onClose()
            }
            return
        }

        // For existing entries, confirm and delete from backend
        if (window.confirm('Are you sure you want to delete this worklog entry?')) {
            await onDelete(entryId)

            // Remove entry from list
            const updatedEntries = entries.filter(e => e.id !== entryId)
            setEntries(updatedEntries)

            // Reset editing state if the deleted entry was being edited
            if (editingEntryId === entryId) {
                setEditingEntryId(null)
            }

            // If no more entries, close the modal
            if (updatedEntries.length === 0) {
                onClose()
            }
        }
    }

    const handleEntryChange = (entryId, field, value) => {
        setEntries(entries.map(entry =>
            entry.id === entryId ? { ...entry, [field]: value } : entry
        ))
    }

    const handleAddEntry = () => {
        // Don't allow adding if there's an entry being edited
        if (editingEntryId !== null) {
            alert('Please save or cancel the current entry before adding a new one')
            return
        }

        const newEntry = {
            id: `new-${Date.now()}`,
            description: '',
            startTime: '09:00',
            endTime: '17:00',
            isEditing: true,
            isNew: true,
            originalData: worklogs[0] // Use first worklog as template for common data
        }

        setEntries([...entries, newEntry])
        setEditingEntryId(newEntry.id)
    }

    const calculateDuration = (startTime, endTime) => {
        if (!startTime || !endTime) return ''

        const start = moment(startTime, 'HH:mm')
        let end = moment(endTime, 'HH:mm')

        let duration = moment.duration(end.diff(start))

        // Handle overnight entries
        if (duration.asMinutes() < 0) {
            end = end.add(1, 'day')
            duration = moment.duration(end.diff(start))
        }

        const hours = Math.floor(duration.asHours())
        const minutes = duration.minutes()

        return `${hours}h ${minutes}m`
    }

    const isOvernightEntry = (startTime, endTime) => {
        if (!startTime || !endTime) return false

        const startParts = startTime.split(':').map(Number)
        const endParts = endTime.split(':').map(Number)

        const startSeconds = startParts[0] * 3600 + startParts[1] * 60
        const endSeconds = endParts[0] * 3600 + endParts[1] * 60

        return endSeconds <= startSeconds
    }

    if (!isOpen) return null

    const commonData = getCommonData()
    if (!commonData) return null

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>Worklog Entries</h2>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        ×
                    </button>
                </div>

                {/* Frozen Fields Section */}
                <div className={styles.frozenFieldsSection}>
                    <div className={styles.frozenFieldGroup}>
                        <label className={styles.frozenLabel}>Assigned to:</label>
                        <div className={styles.frozenText}>{commonData.user}</div>
                    </div>

                    <div className={styles.frozenFieldGroup}>
                        <label className={styles.frozenLabel}>Project:</label>
                        <div className={styles.frozenText}>
                            <div className={styles.frozenProjectDisplay}>
                                <div
                                    className={styles.projectColorDot}
                                    style={{ backgroundColor: commonData.projectColor }}
                                />
                                {commonData.project}
                            </div>
                        </div>
                    </div>

                    {commonData.task && (
                        <div className={styles.frozenFieldGroup}>
                            <label className={styles.frozenLabel}>Task:</label>
                            <div className={styles.frozenText}>{commonData.task}</div>
                        </div>
                    )}

                    <div className={styles.frozenFieldGroup}>
                        <label className={styles.frozenLabel}>Date:</label>
                        <div className={styles.frozenText}>
                            {moment(commonData.date).format('dddd, MMMM D, YYYY')}
                        </div>
                    </div>

                    <div className={styles.frozenFieldGroup}>
                        <label className={styles.frozenLabel}>Billable:</label>
                        <div className={styles.frozenText}>
                            {commonData.billable ? 'Yes' : 'No'}
                        </div>
                    </div>
                </div>

                {/* Scrollable Entries Section */}
                <div className={styles.entriesSection}>
                    <div className={styles.entriesSectionHeader}>
                        <h3>Time Entries ({entries.length})</h3>
                    </div>

                    <div className={styles.entriesTable}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th className={styles.thNumber}>#</th>
                                    <th className={styles.thDescription}>Description</th>
                                    <th className={styles.thTime}>Start</th>
                                    <th className={styles.thTime}>End</th>
                                    <th className={styles.thDuration}>Duration</th>
                                    <th className={styles.thActions}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entries.map((entry, index) => (
                                    entry.isEditing ? (
                                        <tr key={entry.id} className={styles.editingRow}>
                                            <td className={styles.tdNumber}>{index + 1}</td>
                                            <td className={styles.tdDescription}>
                                                <input
                                                    type="text"
                                                    className={styles.inlineInput}
                                                    value={entry.description}
                                                    onChange={(e) => handleEntryChange(entry.id, 'description', e.target.value)}
                                                    placeholder="Enter description"
                                                />
                                            </td>
                                            <td className={styles.tdTime}>
                                                <input
                                                    type="time"
                                                    className={styles.inlineTimeInput}
                                                    value={entry.startTime}
                                                    onChange={(e) => handleEntryChange(entry.id, 'startTime', e.target.value)}
                                                />
                                            </td>
                                            <td className={styles.tdTime}>
                                                <input
                                                    type="time"
                                                    className={styles.inlineTimeInput}
                                                    value={entry.endTime}
                                                    onChange={(e) => handleEntryChange(entry.id, 'endTime', e.target.value)}
                                                />
                                                {isOvernightEntry(entry.startTime, entry.endTime) && (
                                                    <span className={styles.overnightBadge}>+1</span>
                                                )}
                                            </td>
                                            <td className={styles.tdDuration}>
                                                {calculateDuration(entry.startTime, entry.endTime)}
                                            </td>
                                            <td className={styles.tdActions}>
                                                <div className={styles.actionButtons}>
                                                    <button
                                                        type="button"
                                                        className={styles.saveButtonSmall}
                                                        onClick={() => handleSaveEntry(entry.id)}
                                                        title="Save"
                                                    >
                                                        ✓
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={styles.cancelButtonSmall}
                                                        onClick={() => handleCancelEdit(entry.id)}
                                                        title="Cancel"
                                                    >
                                                        ×
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={styles.deleteButtonSmall}
                                                        onClick={() => handleDeleteEntry(entry.id)}
                                                        title="Delete"
                                                    >
                                                        🗑
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : (
                                        <tr key={entry.id} className={styles.viewRow}>
                                            <td className={styles.tdNumber}>{index + 1}</td>
                                            <td className={styles.tdDescription}>{entry.description}</td>
                                            <td className={styles.tdTime}>{entry.startTime}</td>
                                            <td className={styles.tdTime}>
                                                {entry.endTime}
                                                {isOvernightEntry(entry.startTime, entry.endTime) && (
                                                    <span className={styles.overnightBadge}>+1</span>
                                                )}
                                            </td>
                                            <td className={styles.tdDuration}>
                                                {calculateDuration(entry.startTime, entry.endTime)}
                                            </td>
                                            <td className={styles.tdActions}>
                                                <button
                                                    type="button"
                                                    className={styles.editButtonSmall}
                                                    onClick={() => handleEditClick(entry.id)}
                                                    disabled={editingEntryId !== null && editingEntryId !== entry.id}
                                                    title="Edit"
                                                >
                                                    ✎
                                                </button>
                                            </td>
                                        </tr>
                                    )
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Add Entry Button */}
                    <div className={styles.addEntryButtonContainer}>
                        <button
                            type="button"
                            className={styles.addEntryButton}
                            onClick={handleAddEntry}
                            disabled={editingEntryId !== null}
                        >
                            + Add Entry
                        </button>
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <button
                        type="button"
                        className={styles.closeModalButton}
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    )
}